import { initAudio, startBGM } from './game/audio';

const STORAGE_KEY = 'avengers-match:passcode-unlocked-at';
const UNLOCK_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const EXPECTED_HASH = (import.meta.env.VITE_PASSCODE_HASH ?? '').toLowerCase();

function primeAudio() {
  initAudio();
  startBGM();
}

export function mountPasscodeGate(onUnlock: () => void) {
  if (isWithinUnlockWindow()) {
    document.addEventListener(
      'pointerdown',
      () => primeAudio(),
      { once: true },
    );
    onUnlock();
    return;
  }
  renderGate(() => {
    primeAudio();
    onUnlock();
  });
}

function isWithinUnlockWindow(): boolean {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < UNLOCK_WINDOW_MS;
}

async function sha256Hex(s: string): Promise<string> {
  const buf = new TextEncoder().encode(s);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function renderGate(onUnlock: () => void) {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  const style = document.createElement('style');
  style.textContent = `
    .pg-root { position:fixed; inset:0; display:flex; align-items:center; justify-content:center; background:#0a0f1f; }
    .pg-card { display:flex; flex-direction:column; align-items:center; gap:28px; padding:40px 48px; border-radius:24px; background:#121a32; box-shadow:0 20px 60px rgba(0,0,0,.5); }
    .pg-title { font-size:28px; font-weight:700; color:#ffd24a; letter-spacing:.5px; }
    .pg-dots { display:flex; gap:18px; }
    .pg-dot { width:20px; height:20px; border-radius:50%; background:#2a3558; transition:background .12s ease, transform .12s ease; }
    .pg-dot.filled { background:#ffd24a; transform:scale(1.1); }
    .pg-dot.error { background:#ff6b6b; }
    .pg-pad { display:grid; grid-template-columns:repeat(3, 80px); gap:16px; }
    .pg-key { width:80px; height:80px; border-radius:50%; border:none; background:#1b2747; color:#f4f4f4; font-size:32px; font-weight:600; cursor:pointer; -webkit-tap-highlight-color:transparent; transition:background .1s ease, transform .05s ease; }
    .pg-key:active { background:#2a3b66; transform:scale(.94); }
    .pg-key.pg-key-clear { background:transparent; color:#8ca0c4; font-size:18px; }
    .pg-key.pg-key-blank { visibility:hidden; }
  `;
  document.head.appendChild(style);

  const root = document.createElement('div');
  root.className = 'pg-root';
  root.innerHTML = `
    <div class="pg-card">
      <div class="pg-title">Enter passcode</div>
      <div class="pg-dots">
        <div class="pg-dot"></div><div class="pg-dot"></div><div class="pg-dot"></div><div class="pg-dot"></div>
      </div>
      <div class="pg-pad"></div>
    </div>
  `;
  app.appendChild(root);

  const dots = Array.from(root.querySelectorAll<HTMLDivElement>('.pg-dot'));
  const pad = root.querySelector<HTMLDivElement>('.pg-pad')!;

  const keys: Array<{ label: string; value: string; kind?: 'digit' | 'clear' | 'blank' }> = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '', value: '', kind: 'blank' },
    { label: '0', value: '0' },
    { label: 'clear', value: 'clear', kind: 'clear' },
  ];

  let entered = '';
  let locked = false;

  const updateDots = (flashError = false) => {
    dots.forEach((d, i) => {
      d.classList.toggle('filled', i < entered.length && !flashError);
      d.classList.toggle('error', flashError);
    });
  };

  const onDigit = async (digit: string) => {
    if (locked || entered.length >= 4) return;
    entered += digit;
    updateDots();
    if (entered.length === 4) {
      locked = true;
      const hash = await sha256Hex(entered);
      if (hash === EXPECTED_HASH) {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        root.remove();
        style.remove();
        onUnlock();
      } else {
        updateDots(true);
        setTimeout(() => {
          entered = '';
          locked = false;
          updateDots();
        }, 500);
      }
    }
  };

  const onClear = () => {
    if (locked) return;
    entered = '';
    updateDots();
  };

  for (const k of keys) {
    const btn = document.createElement('button');
    btn.className = 'pg-key' + (k.kind === 'clear' ? ' pg-key-clear' : '') + (k.kind === 'blank' ? ' pg-key-blank' : '');
    btn.textContent = k.label;
    btn.type = 'button';
    if (k.kind === 'clear') btn.addEventListener('click', onClear);
    else if (!k.kind) btn.addEventListener('click', () => void onDigit(k.value));
    pad.appendChild(btn);
  }
}
